import trackingData from '../data/trackingData.json';

/**
 * Get tracking data by tracking number
 * @param {string} trackingNumber - The tracking number to search for
 * @returns {Object|null} - Tracking data or null if not found
 */
export const getTrackingData = (trackingNumber) => {
    return trackingData.sampleTrackingData[trackingNumber.trim()] || null;
};

/**
 * Get status information from status definitions
 * @param {string} status - The status key
 * @returns {Object} - Status information with title, description, icon, and color
 */
export const getStatusInfo = (status) => {
    return trackingData.statusDefinitions[status] || {
        title: status,
        description: 'Status information not available',
        icon: 'FaBox',
        color: '#6B7280'
    };
};

/**
 * Format date string to readable format
 * @param {string} dateString - ISO date string
 * @returns {string} - Formatted date string
 */
export const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
};

/**
 * Process tracking history to remove duplicates and create timeline
 * @param {Array} history - Array of tracking history items
 * @param {string} currentStatus - Current status of the shipment
 * @returns {Array} - Processed timeline array
 */
export const processTrackingHistory = (history, currentStatus) => {
    if (!history || !Array.isArray(history)) return [];

    // Sort by timestamp to ensure chronological order
    const sortedHistory = [...history].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

    // Remove duplicate entries based on status only (keep the latest timestamp for each status)
    const uniqueHistory = sortedHistory.reduce((acc, current) => {
        const existingIndex = acc.findIndex(item => item.status === current.status);
        if (existingIndex !== -1) {
            // Replace with the newer entry if current has a later timestamp
            if (new Date(current.timestamp) > new Date(acc[existingIndex].timestamp)) {
                acc[existingIndex] = current;
            }
        } else {
            acc.push(current);
        }
        return acc;
    }, []);

    // Sort again after deduplication
    const finalHistory = uniqueHistory.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

    return finalHistory.map((item, index) => {
        const statusInfo = getStatusInfo(item.status);
        // Mark as active if this is the current status or any previous status
        const isActive = item.status === currentStatus ||
            finalHistory.findIndex(h => h.status === currentStatus) >= index;

        return {
            title: statusInfo.title,
            description: item.description || statusInfo.description,
            icon: statusInfo.icon,
            time: formatDate(item.timestamp),
            isActive: isActive,
            location: item.location || 'N/A'
        };
    });
};

/**
 * Get API endpoint URL
 * @param {string} endpointKey - The endpoint key from trackingData
 * @returns {string} - Complete API endpoint URL
 */
export const getApiEndpoint = (endpointKey) => {
    return `${trackingData.apiEndpoints.baseUrl}${trackingData.apiEndpoints.endpoints[endpointKey]}`;
};

/**
 * Validate tracking number format
 * @param {string} trackingNumber - The tracking number to validate
 * @returns {boolean} - True if valid format
 */
export const validateTrackingNumber = (trackingNumber) => {
    // SDD tracking numbers typically follow pattern: numbers + SDD + numbers
    const pattern = /^\d+SDD\d+$/;
    return pattern.test(trackingNumber.trim());
};

/**
 * Get all available tracking numbers for testing
 * @returns {Array} - Array of available tracking numbers
 */
export const getAvailableTrackingNumbers = () => {
    return Object.keys(trackingData.sampleTrackingData);
};
